import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useSearchParams, useParams, Link } from 'react-router-dom';
import { CheckCircle, Menu, X } from 'lucide-react';
import { WaitlistForm } from './components/WaitlistForm';
import { DemoNavbar } from './components/DemoNavbar';
import { DemoPageWrapper } from './components/DemoPageWrapper';
import { AuthPage } from './components/AuthPage';
import { SocialAuthPage } from './components/SocialAuthPage';
import { Dashboard } from './components/Dashboard';
import { AdminPanel } from './components/AdminPanel';
import { UserAccount } from './components/UserAccount';
import { ModuleContent } from './components/ModuleContent';
import { LessonContent } from './components/LessonContent';
import { Module1Content } from './components/Module1Content';
import { Module1ContentNew } from './components/Module1ContentNew';
import { Module2Content } from './components/Module2Content';
import { Module3Content } from './components/Module3Content';
import { Module4Content } from './components/Module4Content';
import { Module5Content } from './components/Module5Content';
import { Module6Content } from './components/Module6Content';
import { Module7Content } from './components/Module7Content';
import { Module8Content } from './components/Module8Content';
import { ProgressDashboard } from './components/ProgressDashboard';
import { AdminContentManager } from './components/AdminContentManager';
import { AdminWaitlist } from './components/AdminWaitlist';
import { AdminNavbar } from './components/navbars/AdminNavbar';
import { UserNavbar } from './components/navbars/UserNavbar';
import { DataProtection } from './components/DataProtection';
import { InterestFormModal } from './components/InterestFormModal';
import { WaitingListPage } from './components/WaitingListPage';
import { ResetPasswordPage } from './components/ResetPasswordPage';
import { NotFoundPage } from './components/NotFoundPage';
import { apiUrl } from './config/api';
import { initializeClarity } from './lib/clarity';
import { initializeAnalytics } from './utils/analytics';
import { EmailInput } from './components/EmailInput';
import { validateEmail, validateEmailMatch } from './utils/emailValidation';
import { CourseFeedbackModal } from './components/CourseFeedbackModal';
import { FAQPage } from './components/FAQPage';
import { CoursesPage } from './components/CoursesPage';
import { FreeCourseSignupPage } from './components/FreeCourseSignupPage';

interface User {
  id: string;
  email: string;
  fullName: string;
  firstName?: string;
  lastName?: string;
  company: string;
  role: string;
  userType: string;
  status: string;
  userSource?: string;
  lastActivityAt?: string;
  lastInactivityEmailAt?: string;
}

// ============================================================================
// MAIN APP COMPONENT
// ============================================================================

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [showInterestForm, setShowInterestForm] = useState(false);
  const [interestFormData, setInterestFormData] = useState<{
    invitationEmail?: string;
    inviteToken?: string | null;
    formType?: 'request-access' | 'team-invitation';
  }>({});
  const userRef = useRef<User | null>(null);

  useEffect(() => {
    const clarityId = process.env.REACT_APP_MS_CLARITY_ID;
    if (process.env.NODE_ENV === 'production') {
      initializeClarity(clarityId);
      initializeAnalytics();
    }

    const handleProfileUpdate = (event: Event) => {
      const detail = (event as CustomEvent<Partial<User>>).detail;
      if (detail) {
        setUser(prev => prev ? { ...prev, ...detail } : prev);
      }
    };

    window.addEventListener('userProfileUpdated', handleProfileUpdate as EventListener);
    return () => {
      window.removeEventListener('userProfileUpdated', handleProfileUpdate as EventListener);
    };
  }, []);

  useEffect(() => {
    // Only check auth status if not on landing page
    const currentPath = window.location.pathname;
    if (currentPath !== '/') {
      checkAuthStatus();
    } else {
      setLoading(false);
    }

    // Listen for showInterestForm events
    const handleShowInterestForm = (event: CustomEvent) => {
      setInterestFormData({
        invitationEmail: event.detail?.invitationEmail || userRef.current?.email || '',
        inviteToken: event.detail?.inviteToken || null,
        formType: event.detail?.formType || 'request-access'
      });
      setShowInterestForm(true);
    };

    window.addEventListener('showInterestForm', handleShowInterestForm as EventListener);
    
    return () => {
      window.removeEventListener('showInterestForm', handleShowInterestForm as EventListener);
    };
  }, []);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await fetch(apiUrl('/api/auth/me'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData.data.user);
      } else {
        // Clear invalid token
        localStorage.removeItem('authToken');
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      // Clear token on error
      localStorage.removeItem('authToken');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (email: string, password: string) => {
    try {
      const response = await fetch(apiUrl('/api/auth/login'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (data.success) {
        setUser(data.data.user);
        localStorage.setItem('authToken', data.data.token);
        return { success: true };
      } else {
        return { success: false, message: data.message };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, message: 'Login failed' };
    }
  };

  const handleRegister = async (email: string, password: string, fullName: string, company: string) => {
    try {
      const response = await fetch(apiUrl('/api/auth/register'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password, fullName, company }),
      });

      const data = await response.json();

      if (data.success) {
        setUser(data.data.user);
        localStorage.setItem('authToken', data.data.token);
        return { success: true };
      } else {
        return { success: false, message: data.message };
      }
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, message: 'Registration failed' };
    }
  };

  const handleSocialAuth = async (provider: string, token: string) => {
    // This function is no longer used since we redirect to OAuth endpoints
    // Keeping it for backward compatibility but it won't be called
    return { success: false, message: 'Use OAuth redirect instead' };
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('demoToken');
    localStorage.removeItem('userType');
    // Redirect to landing page after logout
    window.location.href = '/';
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: 'var(--dark)',
        color: 'var(--light)'
      }}>
        Loading...
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        {/* ====================================================================
            PUBLIC ROUTES
        ==================================================================== */}
        
        {/* Landing page with invitation support */}
        <Route 
          path="/" 
          element={<PublicLandingPage />} 
        />

        {/* Authentication page */}
        <Route 
          path="/auth" 
          element={
            user ? (
              user.role === 'admin' ? (
                <Navigate to="/admin/dashboard" replace />
              ) : (
                <Navigate to={`/user/${user.id}/dashboard`} replace />
              )
            ) : (
              <AuthPage onLogin={handleLogin} onRegister={handleRegister} />
            )
          } 
        />

        {/* Password reset page */}
        <Route 
          path="/reset-password" 
          element={<ResetPasswordPage />} 
        />

        {/* Social Authentication page for demo users */}
        <Route 
          path="/social-auth" 
          element={
            <SocialAuthPage onSocialAuth={handleSocialAuth} />
          } 
        />

        {/* Data Protection Policy page */}
        <Route 
          path="/data" 
          element={<DataProtection />} 
        />

        {/* FAQ Page for GEO/SEO */}
        <Route 
          path="/faq" 
          element={<FAQPage />} 
        />

        {/* Courses Page for SEO */}
        <Route 
          path="/courses" 
          element={<CoursesPage />} 
        />

        {/* Free Course Signup Page for SEO */}
        <Route 
          path="/free-course-signup" 
          element={<FreeCourseSignupPage />} 
        />

        {/* Waiting List Page */}
        <Route 
          path="/advanced/waiting-list" 
          element={<WaitingListPage />} 
        />

        {/* ====================================================================
            ADMIN ROUTES - /admin/*
        ==================================================================== */}
        
        <Route 
          path="/admin/dashboard" 
          element={
            user && user.role === 'admin' ? (
              <AdminLayout user={user} onLogout={handleLogout}>
                <Dashboard user={user} onLogout={handleLogout} />
              </AdminLayout>
            ) : (
              <Navigate to="/auth" replace />
            )
          } 
        />

        <Route 
          path="/admin/teams" 
          element={
            user && user.role === 'admin' ? (
              <AdminLayout user={user} onLogout={handleLogout}>
                <AdminPanel user={user} onLogout={handleLogout} />
              </AdminLayout>
            ) : (
              <Navigate to="/auth" replace />
            )
          } 
        />

        <Route 
          path="/admin/invitations" 
          element={
            user && user.role === 'admin' ? (
              <AdminLayout user={user} onLogout={handleLogout}>
                <AdminPanel user={user} onLogout={handleLogout} />
              </AdminLayout>
            ) : (
              <Navigate to="/auth" replace />
            )
          } 
        />

        <Route 
          path="/admin/content" 
          element={
            user && user.role === 'admin' ? (
              <AdminLayout user={user} onLogout={handleLogout}>
                <AdminContentManager user={user} onLogout={handleLogout} />
              </AdminLayout>
            ) : (
              <Navigate to="/auth" replace />
            )
          } 
        />

        <Route 
          path="/admin/module1" 
          element={
            user && user.role === 'admin' ? (
              <AdminLayout user={user} onLogout={handleLogout}>
                <Module1Content user={user} onLogout={handleLogout} isAdminMode={true} />
              </AdminLayout>
            ) : (
              <Navigate to="/auth" replace />
            )
          } 
        />

        <Route 
          path="/admin/progress" 
          element={
            user && user.role === 'admin' ? (
              <AdminLayout user={user} onLogout={handleLogout}>
                <ProgressDashboard user={user} onLogout={handleLogout} />
              </AdminLayout>
            ) : (
              <Navigate to="/auth" replace />
            )
          } 
        />

        <Route 
          path="/admin/resources" 
          element={
            user && user.role === 'admin' ? (
              <AdminLayout user={user} onLogout={handleLogout}>
                <div style={{ padding: '2rem', textAlign: 'center' }}>
                  <h1>Admin Resources</h1>
                  <p>Resource management tools for administrators</p>
                </div>
              </AdminLayout>
            ) : (
              <Navigate to="/auth" replace />
            )
          } 
        />

        <Route 
          path="/admin/waitlist" 
          element={
            user && user.role === 'admin' ? (
              <AdminLayout user={user} onLogout={handleLogout}>
                <AdminWaitlist user={user} onLogout={handleLogout} />
              </AdminLayout>
            ) : (
              <Navigate to="/auth" replace />
            )
          } 
        />

        {/* ====================================================================
            INFO PAGE - /preview/demo (Course Information)
        ==================================================================== */}
        
        <Route 
          path="/preview/demo" 
          element={<DemoLayout />} 
        />

        {/* ====================================================================
            FULL ACCESS USER ROUTES - /user/:userId/*
        ==================================================================== */}
        
        <Route 
          path="/user/:userId/dashboard" 
          element={<UserDashboardRoute user={user} onLogout={handleLogout} />}
        />

        <Route 
          path="/user/:userId/progress" 
          element={<UserProgressRoute user={user} onLogout={handleLogout} />}
        />

        <Route 
          path="/user/:userId/resources" 
          element={<UserResourcesRoute user={user} onLogout={handleLogout} />}
        />

        <Route 
          path="/user/:userId/account" 
          element={<UserAccountRoute user={user} onLogout={handleLogout} />}
        />

        <Route 
          path="/user/:userId/module/:moduleId" 
          element={<UserModuleRoute user={user} onLogout={handleLogout} />}
        />

        <Route 
          path="/user/:userId/lesson/:lessonId" 
          element={<UserLessonRoute user={user} onLogout={handleLogout} />}
        />

        <Route 
          path="/user/:userId/module1" 
          element={<UserModule1Route user={user} onLogout={handleLogout} />}
        />

        <Route 
          path="/user/:userId/module2" 
          element={<UserModule2Route user={user} onLogout={handleLogout} />}
        />

        <Route 
          path="/user/:userId/module3" 
          element={<UserModule3Route user={user} onLogout={handleLogout} />}
        />

        <Route 
          path="/user/:userId/module4" 
          element={<UserModule4Route user={user} onLogout={handleLogout} />}
        />
        <Route 
          path="/user/:userId/module5" 
          element={<UserModule5Route user={user} onLogout={handleLogout} />}
        />
        <Route 
          path="/user/:userId/module6" 
          element={<UserModule6Route user={user} onLogout={handleLogout} />}
        />
        <Route 
          path="/user/:userId/module7" 
          element={<UserModule7Route user={user} onLogout={handleLogout} />}
        />
        <Route 
          path="/user/:userId/module8" 
          element={<UserModule8Route user={user} onLogout={handleLogout} />}
        />

        {/* ====================================================================
            LEGACY ROUTES (for backward compatibility)
        ==================================================================== */}
        
        <Route 
          path="/dashboard" 
          element={
            user ? (
              <Navigate to={user.role === 'admin' ? '/admin/dashboard' : `/user/${user.id}/dashboard`} replace />
            ) : (
              <Navigate to="/auth" replace />
            )
          } 
        />

        <Route 
          path="/module1" 
          element={
            user ? (
              <Navigate to={user.role === 'admin' ? '/admin/module1' : `/user/${user.id}/module1`} replace />
            ) : (
              <Navigate to="/" replace />
            )
          } 
        />

        {/* Catch all - show 404 page for non-existent routes */}
        <Route 
          path="*" 
          element={<NotFoundPage />} 
        />
      </Routes>
      
      {/* Global Interest Form Modal */}
      {showInterestForm && (
        <InterestFormModal
          isOpen={showInterestForm}
          onClose={() => setShowInterestForm(false)}
          invitationEmail={interestFormData.invitationEmail || ''}
          inviteToken={interestFormData.inviteToken}
          formType={interestFormData.formType || 'request-access'}
          currentUser={user}
        />
      )}
    </Router>
  );
}

// ============================================================================
// ROUTE COMPONENTS
// ============================================================================

function UserModule3Route({ user, onLogout }: UserRouteProps) {
  const { userId } = useParams<{ userId: string }>();
  
  if (!user || user.id !== userId) {
    return <Navigate to="/auth" replace />;
  }
  
  // If admin, use AdminLayout to maintain admin navbar
  const Layout = user.role === 'admin' ? AdminLayout : UserLayout;
  const isAdminMode = user.role === 'admin';
  
  return (
    <Layout user={user} onLogout={onLogout}>
      <Module3Content user={user} onLogout={onLogout} isAdminMode={isAdminMode} />
    </Layout>
  );
}

function UserModule4Route({ user, onLogout }: UserRouteProps) {
  const { userId } = useParams<{ userId: string }>();
  
  if (!user || user.id !== userId) {
    return <Navigate to="/auth" replace />;
  }
  
  // If admin, use AdminLayout to maintain admin navbar
  const Layout = user.role === 'admin' ? AdminLayout : UserLayout;
  const isAdminMode = user.role === 'admin';
  
  return (
    <Layout user={user} onLogout={onLogout}>
      <Module4Content user={user} onLogout={onLogout} isAdminMode={isAdminMode} />
    </Layout>
  );
}

function UserModule5Route({ user, onLogout }: UserRouteProps) {
  const { userId } = useParams<{ userId: string }>();
  
  if (!user || user.id !== userId) {
    return <Navigate to="/auth" replace />;
  }
  
  // If admin, use AdminLayout to maintain admin navbar
  const Layout = user.role === 'admin' ? AdminLayout : UserLayout;
  const isAdminMode = user.role === 'admin';
  
  return (
    <Layout user={user} onLogout={onLogout}>
      <Module5Content user={user} onLogout={onLogout} isAdminMode={isAdminMode} />
    </Layout>
  );
}

// ============================================================================
// LAYOUT COMPONENTS
// ============================================================================

interface LayoutProps {
  user: User;
  onLogout: () => void;
  children: React.ReactNode;
}

function AdminLayout({ user, onLogout, children }: LayoutProps) {
  return (
    <div>
      <AdminNavbar user={user} onLogout={onLogout} />
      <main className="dashboard-content">{children}</main>
    </div>
  );
}

type CourseFeedbackResult = 'submitted' | 'dismissed';

interface CourseFeedbackEventDetail {
  source?: 'module' | 'navbar';
  moduleId?: number;
  userEmail?: string;
  onComplete?: (result: CourseFeedbackResult) => void;
}

function UserLayout({ user, onLogout, children }: LayoutProps) {
  const [feedbackModalState, setFeedbackModalState] = useState<{
    isOpen: boolean;
    origin: 'module' | 'navbar';
    moduleId?: number;
    userEmail?: string;
    onComplete?: (result: CourseFeedbackResult) => void;
  }>({
    isOpen: false,
    origin: 'navbar',
    userEmail: user.email
  });

  const handleInviteColleagues = () => {
    const event = new CustomEvent('showInterestForm', {
      detail: { formType: 'team-invitation', invitationEmail: user.email }
    });
    window.dispatchEvent(event);
  };

  useEffect(() => {
    const handleFeedbackEvent = (event: Event) => {
      const customEvent = event as CustomEvent<CourseFeedbackEventDetail>;
      const detail = customEvent.detail || {};

      setFeedbackModalState({
        isOpen: true,
        origin: detail.source === 'module' ? 'module' : 'navbar',
        moduleId: detail.moduleId,
        userEmail: detail.userEmail || user.email,
        onComplete: detail.onComplete
      });
    };

    window.addEventListener('showCourseFeedback', handleFeedbackEvent as EventListener);
    return () => {
      window.removeEventListener('showCourseFeedback', handleFeedbackEvent as EventListener);
    };
  }, [user]);

  const openFeedbackFromNavbar = () => {
    setFeedbackModalState({
      isOpen: true,
      origin: 'navbar',
      moduleId: undefined,
      userEmail: user.email,
      onComplete: undefined
    });
  };

  const handleFeedbackComplete = (result: CourseFeedbackResult) => {
    setFeedbackModalState(prevState => {
      const { userEmail: modalEmail, moduleId, onComplete } = prevState;
      const emailForKey = modalEmail || user.email;

      if (emailForKey && result === 'submitted') {
        const feedbackStatusKey = `course_feedback_status_${emailForKey}`;
        localStorage.setItem(feedbackStatusKey, 'submitted');
        if (moduleId) {
          localStorage.setItem(`module${moduleId}_feedback_submitted_${emailForKey}`, 'true');
        }
      }

      if (onComplete) {
        try {
          onComplete(result);
        } catch (callbackError) {
          console.error('Error running feedback completion callback:', callbackError);
        }
      }

      return {
        ...prevState,
        isOpen: false,
        onComplete: undefined
      };
    });
  };

  return (
    <div>
      <UserNavbar
        user={user}
        onLogout={onLogout}
        onInviteColleagues={handleInviteColleagues}
        onOpenFeedback={openFeedbackFromNavbar}
      />
      <main className="dashboard-content">{children}</main>
      <CourseFeedbackModal
        isOpen={feedbackModalState.isOpen}
        userEmail={feedbackModalState.userEmail || user.email}
        origin={feedbackModalState.origin}
        onComplete={handleFeedbackComplete}
      />
    </div>
  );
}

function DemoLayout() {
  // Public demo access - no token required
  return (
    <div>
      <DemoNavbar />
      <DemoPageWrapper />
    </div>
  );
}


// ============================================================================
// WRAPPER COMPONENTS
// ============================================================================

function PublicLandingPage() {
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [waitlistModal, setWaitlistModal] = useState<{isOpen: boolean, courseType: 'advanced' | 'premium' | null}>({
    isOpen: false,
    courseType: null
  });
  const [waitlistSuccess, setWaitlistSuccess] = useState(false);
  const [showEmailSignup, setShowEmailSignup] = useState(false);

  const openWaitlist = (courseType: 'advanced' | 'premium') => {
    setWaitlistModal({ isOpen: true, courseType });
  };

  const closeWaitlist = () => {
    setWaitlistModal({ isOpen: false, courseType: null });
  };

  const handleWaitlistSuccess = () => {
    setWaitlistSuccess(true);
    setTimeout(() => {
      setWaitlistSuccess(false);
      closeWaitlist();
    }, 2000);
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };
  
  if (inviteToken) {
    // Redirect to preview route
    return <Navigate to={`/preview/${inviteToken}`} replace />;
  }
  
  return (
    <div className="landing-page">
      {/* Header */}
      <div className="landing-header">
        <div className="landing-container">
        <div className="landing-logo">
          <img 
              src="/logo-gold-small.png" 
            alt="recberry" 
            className="landing-logo-image"
          />
        </div>
        
        {/* Hamburger Menu Button */}
        <button className="landing-hamburger-btn" onClick={toggleMenu}>
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
        
        {/* Desktop Navigation */}
        <div className="landing-nav desktop-nav">
          <button 
            onClick={() => window.location.href = '/preview/demo'}
            className="btn btn-secondary"
          >
            Preview Free Course
          </button>
          <button 
            onClick={() => window.location.href = '/auth'}
            className="btn btn-primary"
          >
            LOG IN
          </button>
        </div>
        </div>
      </div>
      
      {/* Mobile Overlay */}
      <div className={`landing-mobile-overlay ${isMenuOpen ? 'open' : ''}`} onClick={toggleMenu}></div>
      
      {/* Mobile Side Menu */}
      <div className={`landing-mobile-menu ${isMenuOpen ? 'open' : ''}`}>
        <button 
          onClick={() => {
            window.location.href = '/preview/demo';
            setIsMenuOpen(false);
          }}
          className="landing-mobile-btn btn-secondary"
        >
          Preview Free Course
        </button>
        <button 
          onClick={() => {
            window.location.href = '/auth';
            setIsMenuOpen(false);
          }}
          className="landing-mobile-btn btn-primary"
        >
          LOG IN
        </button>
      </div>

      {/* Hero Section */}
      <div className="landing-hero">
        <div className="hero-content">
          <div className="hero-badge">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20,6 9,17 4,12"/>
            </svg>
            <span>Proven Results</span>
          </div>
          <h1 className="hero-title">
            <span className="transform-text">Transform</span> Your Team into <br />
            <span className="senior-text"> Senior</span> <span className="talent-text">Talent Magnets</span>
          </h1>
          <p className="hero-description">
            Master the art of hiring principal IT talent with cutting-edge training courses developed by a Principal IT Recruiter with 20+ years at top tech companies. Start free, level up, and dominate recruitment in your market.
          </p>
          
          <div className="hero-credibility">
            <div className="credibility-badge">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 12l2 2 4-4"/>
                <path d="M21 12c-1 0-3-1-3-3s2-3 3-3 3 1 3 3-2 3-3 3"/>
                <path d="M3 12c1 0 3-1 3-3s-2-3-3-3-3 1-3 3 2 3 3 3"/>
                <path d="M12 3c0 1-1 3-3 3s-3-2-3-3 1-3 3-3 3 2 3 3"/>
                <path d="M12 21c0-1 1-3 3-3s3 2 3 3-1 3-3 3-3-2-3-3"/>
              </svg>
              <span>Built on the <span style={{color: 'var(--orange)'}}>Toughest</span> Recruitment Field</span>
            </div>
            <p className="credibility-text">
              <strong>IT talent recruitment is considered the most challenging field in recruitment.</strong> 
              These proven strategies are easily transferable to hiring principal sales, finance, and other senior roles.
            </p>
          </div>
          
          <div className="hero-stats">
            <div className="stat-item">
              <div className="stat-number">63%</div>
              <div className="stat-label">Czech companies struggle with senior IT talent</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">74%</div>
              <div className="stat-label">US firms face critical talent shortages</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">68 days</div>
              <div className="stat-label">Average time-to-hire</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">€400K</div>
              <div className="stat-label">Potential savings in agency fees</div>
            </div>
          </div>
          
          <div className="hero-actions">
          <button 
            onClick={() => setShowEmailSignup(true)}
              className="btn btn-primary btn-large"
          >
            Start Free Course Now
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14m-7-7l7 7-7 7"/>
              </svg>
            </button>
            <button 
              onClick={() => {
                const coursesSection = document.querySelector('.landing-courses');
                if (coursesSection) {
                  coursesSection.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              className="btn btn-secondary btn-large"
            >
              Review All Courses
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
                <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
              </svg>
          </button>
          </div>
          
          <div className="hero-trust">
            <p>Trusted by TA leaders at leading tech companies</p>
            <div className="trust-logos">
              <span>Avast</span>
              <span>Skype</span>
              <span>Akamai</span>
              <span>Suse Linux</span>
            </div>
          </div>
        </div>
      </div>

      {/* Problem Section */}
      <div className="landing-problem">
        <div className="landing-container">
          <div className="section-header">
            <div className="section-badge">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                <path d="M12 17h.01"/>
              </svg>
              <span>The Challenge</span>
            </div>
            <div className="section-header-content">
              <div className="section-header-left">
                <h2 className="section-title">
                  Are You Struggling to Hire Elite IT Talent?
                </h2>
              </div>
              <div className="section-header-right">
                <p className="section-description">
                  The recruitment landscape has changed dramatically. IT talent recruitment is widely considered the most challenging field in recruitment. Here's what's happening in 2025 and why traditional approaches are failing.
                </p>
              </div>
            </div>
          </div>
          
          
          <div className="problem-cards">
            <div className="problem-card">
              <h3 className="problem-title">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="15" y1="9" x2="9" y2="15"/>
                  <line x1="9" y1="9" x2="15" y2="15"/>
                </svg>
                Missed Opportunities
              </h3>
              <p className="problem-description">
                Generic outreach fails to attract principal talent, leaving you competing with Big Tech like Google or JetBrains. Your messages get lost in the noise.
              </p>
              <div className="problem-impact">
                <div className="impact-label">Impact</div>
                <div className="impact-text">5% response rate from top talent</div>
            </div>
            </div>
            
            <div className="problem-card">
              <h3 className="problem-title">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12,6 12,12 16,14"/>
                </svg>
                Time-Consuming Hires
              </h3>
              <p className="problem-description">
                Reactive recruiting drags time-to-hire beyond 60 days, delaying critical projects and frustrating hiring managers who need results now.
              </p>
              <div className="problem-impact">
                <div className="impact-label">Impact</div>
                <div className="impact-text">Project delays & increased costs</div>
              </div>
            </div>
            
            <div className="problem-card">
              <h3 className="problem-title">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
                  <rect x="8" y="2" width="8" height="4" rx="1" ry="1"/>
                  <path d="M12 11h4"/>
                  <path d="M12 16h4"/>
                  <path d="M8 11h.01"/>
                  <path d="M8 16h.01"/>
                </svg>
                Undertrained Teams
              </h3>
              <p className="problem-description">
                Without advanced skills, your team struggles to navigate 2025 trends like hybrid work 2.0 and AI-driven sourcing, creating problems that compound over time.
              </p>
              <div className="problem-impact">
                <div className="impact-label">Impact</div>
                <div className="impact-text">Outdated methods & missed candidates</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Solution Section */}
      <div className="landing-solution">
        <div className="landing-container">
          <div className="solution-content">
            <div className="solution-text">
              <div className="section-badge">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 12l2 2 4-4"/>
                  <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9c1.5 0 2.91.37 4.15 1.02"/>
                </svg>
                <span>The Solution</span>
              </div>
              <h2 className="solution-title">
                Your Path to Recruitment Mastery
              </h2>
              <p className="solution-description">
                Discover a tiered training system designed by Barbora Jensik, who's placed elite talent at cybersecurity firms, high-tech companies, and many tech companies. Built on the toughest recruitment field (IT talent), these strategies are easily transferable to hiring principal sales, finance, and other senior roles. From free basics to premium transformation, our courses empower TA leaders to upskill their teams and dominate senior hiring across all markets.
              </p>
              <div className="solution-benefits">
                <div className="benefit-item">
                  <CheckCircle size={16} color="#10b981" />
                  <span>Proven methodologies from 20+ years experience</span>
                </div>
                <div className="benefit-item">
                  <CheckCircle size={16} color="#10b981" />
                  <span>Data-driven strategies for 2026 market trends</span>
                </div>
                <div className="benefit-item">
                  <CheckCircle size={16} color="#10b981" />
                  <span>Scalable training from individual to team level</span>
                </div>
              </div>
            </div>
            <div className="solution-visual">
              <div className="solution-card">
                <div className="solution-card-header">
                  <div className="expert-avatar">
                    <span>BJ</span>
                  </div>
                  <div className="expert-info">
                    <h4>Barbora Jensik</h4>
                    <p>Principal IT Recruiter</p>
                  </div>
                </div>
                <div className="solution-card-stats">
                  <div className="solution-stat">
                    <span className="solution-stat-number">20+</span>
                    <span className="solution-stat-label">Years of IT Recruitment and Executive Search Experience</span>
                  </div>
                  <div className="solution-stat">
                    <span className="solution-stat-number">500+</span>
                    <span className="solution-stat-label">Placements for mostly IT Companies Globally</span>
                  </div>
                  <div className="solution-stat">
                    <span className="solution-stat-number">€2M+</span>
                    <span className="solution-stat-label">Saved in Missed Opportunities for Clients</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Courses Section */}
      <div className="landing-courses">
        <div className="landing-container">
          <div className="section-header">
            <div className="section-badge">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
                <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
              </svg>
              <span>Learning Paths</span>
            </div>
            <div className="section-header-content">
              <div className="section-header-left">
                <h2 className="section-title">
                  Choose Your Journey to Recruitment Mastery
                </h2>
              </div>
              <div className="section-header-right">
                <p className="section-description">
                  From beginner-friendly foundations to advanced strategic mastery, our tiered approach ensures your team gets exactly the training they need.
                </p>
              </div>
            </div>
          </div>
          
          <div className="course-cards">
            <div className="course-card course-card-free">
              <div className="course-badge">
                <span>FREE</span>
              </div>
              <div className="course-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
                  <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
                </svg>
              </div>
              <h3 className="course-title">Introduction to Senior IT Hiring</h3>
              <p className="course-description">
                Master foundational strategies to source and hire principal IT talent, cutting time-to-hire to &lt;60 days and boosting candidate quality.
              </p>
              <div className="course-features">
                <div className="feature-item">
                  <CheckCircle size={16} color="var(--dark-blue)" />
                  <span>8 beginner-friendly modules</span>
                </div>
                <div className="feature-item">
                  <CheckCircle size={16} color="var(--dark-blue)" />
                  <span>CZ/US market insights (63%/74% shortages)</span>
                </div>
                <div className="feature-item">
                  <CheckCircle size={16} color="var(--dark-blue)" />
                  <span>20% response rate from basic outreach</span>
                </div>
                <div className="feature-item">
                  <CheckCircle size={16} color="var(--dark-blue)" />
                  <span>Saves €15K in agency fees or €150K in lost opportunities</span>
                </div>
              </div>
              <div className="course-cta">
              <button 
                onClick={() => setShowEmailSignup(true)}
                  className="btn btn-primary btn-full"
              >
                Start Free Now
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14m-7-7l7 7-7 7"/>
                  </svg>
              </button>
              </div>
            </div>

            <div className="course-card course-card-advanced">
              <div className="course-badge">
                <span>COMING SOON</span>
              </div>
              <div className="course-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                  <path d="M2 17l10 5 10-5"/>
                  <path d="M2 12l10 5 10-5"/>
                </svg>
              </div>
              <h3 className="course-title">Deep-Dive Strategies</h3>
              <p className="course-description">
                Elevate your team with in-depth tools for sourcing, screening, and hiring seniors. Achieve 35–40% more applications and 30% faster hires.
                <br /><br />
                <em>Expected launch: Q2/Q3 2026</em>
              </p>
              <div className="course-features">
                <div className="feature-item">
                  <CheckCircle size={16} color="var(--dark-blue)" />
                  <span>Multi-page guides, tasks, and examples</span>
                </div>
                <div className="feature-item">
                  <CheckCircle size={16} color="var(--dark-blue)" />
                  <span>Advanced Boolean, A/B testing, and competency models</span>
                </div>
                <div className="feature-item">
                  <CheckCircle size={16} color="var(--dark-blue)" />
                  <span>80% time savings with AI tools</span>
                </div>
                <div className="feature-item">
                  <CheckCircle size={16} color="var(--dark-blue)" />
                  <span>Saves €100K-€150K in fees or €2M+ in lost opportunities</span>
                </div>
              </div>
              <div className="course-cta">
                <button 
                  onClick={() => openWaitlist('advanced')}
                  className="btn btn-secondary btn-full"
                >
                  Join Waitlist
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14m-7-7l7 7-7 7"/>
                  </svg>
                </button>
              </div>
            </div>

            <div className="course-card course-card-premium">
              <div className="course-badge">
                <span>NOT AVAILABLE YET</span>
            </div>
              <div className="course-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
                  <line x1="3" y1="6" x2="21" y2="6"/>
                  <path d="M16 10a4 4 0 0 1-8 0"/>
                </svg>
          </div>
              <h3 className="course-title">Super-Powered Recruiter Masterclass</h3>
              <p className="course-description">
                Transform your team into strategic TA leaders with a premium intensive 1-month program. Master AI-driven sourcing, business development, and client acquisition.
                <br /><br />
                <em>Advanced graduates get discounts • Direct buyers get 1-year Advanced access</em>
              </p>
              <div className="course-features">
                <div className="feature-item">
                  <CheckCircle size={16} color="var(--orange)" />
                  <span>500+ pages, lessons, and 1:1 feedback</span>
                </div>
                <div className="feature-item">
                  <CheckCircle size={16} color="var(--orange)" />
                  <span>50% faster hires, 40% higher retention</span>
                </div>
                <div className="feature-item">
                  <CheckCircle size={16} color="var(--orange)" />
                  <span>Build a scalable recruitment business (3x ROI)</span>
                </div>
                <div className="feature-item">
                  <CheckCircle size={16} color="var(--orange)" />
                  <span>Saves €400K+ in fees or €10M+ in lost opportunities</span>
                </div>
              </div>
              <div className="course-cta">
                <button 
                  onClick={() => openWaitlist('premium')}
                  className="btn btn-premium btn-full"
                >
                  I am interested
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14m-7-7l7 7-7 7"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Why Barbora Section */}
      <div className="landing-why">
        <div className="landing-container">
          <div className="section-badge">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21c0 .55.45 1 1 1h4c.55 0 1-.45 1-1v-1H9v1z"/>
              <path d="M12 2C8.14 2 5 5.14 5 9c0 2.38 1.19 4.47 3 5.74V17c0 .55.45 1 1 1h6c.55 0 1-.45 1-1v-2.26c1.81-1.27 3-3.36 3-5.74 0-3.86-3.14-7-7-7z"/>
            </svg>
            <span>We Offer</span>
          </div>
          <h2>Why Train with us?</h2>
          <div className="why-cards">
            <div className="card">
              <h3>Proven Expertise</h3>
              <p>With 20+ years at premium IT companies, we have placed elite talent like almost impossible to find Malware Analysts, 
                slashing time-to-hire by 47% and boosting response rates by 320%.</p>
            </div>
            <div className="card">
              <h3>Data-Driven Success</h3>
              <p>Our courses leverage 2025 trends and include them in your hiring strategy in 2026 - hybrid work 2.0, AI tools (80% time savings), and competency models - backed by deep market research, LinkedIn Talent Trends 2024, and other sources.</p>
            </div>
            <div className="card">
              <h3>Tailored for Leaders</h3>
              <p>Designed for TA leaders at leading firms, our training equips your team to compete with Big Tech and 
                secure principal talent in CZ and US markets.</p>
            </div>
            <div className="card">
              <h3>Scalable Impact</h3>
              <p>From quick wins (20% response rates) to transformative strategies (3x ROI), our tiered courses empower your team 
                to save €15K–€400K and build a recruitment powerhouse.</p>
            </div>
          </div>
          
          <div style={{ textAlign: 'center', marginTop: '3rem' }}>
            <button 
              onClick={() => window.location.href = '/preview/demo'}
              className="btn btn-secondary btn-large"
            >
              Not convinced yet? Learn more
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
                <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Results Section */}
      <div className="landing-results">
        <div className="landing-container">
          <div className="section-header">
            <div className="section-badge">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
              <span>Client Success</span>
            </div>
            <div className="section-header-content">
              <div className="section-header-left">
                <h2 className="section-title">
                  What Our Clients Say
                </h2>
            </div>
              <div className="section-header-right">
                <p className="section-description">
                  These success stories showcase the proven methodologies we've refined through 500+ placements. Now we're sharing these battle-tested strategies in our training programs.
                </p>
            </div>
            </div>
          </div>
          
          <div className="testimonials-grid">
            <div className="testimonial-card testimonial-featured">
              <div className="testimonial-content">
                <div className="quote-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z"/>
                    <path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z"/>
                  </svg>
            </div>
                <blockquote className="testimonial-text">
                  "I rely on Barbora already for more than 2 years. She is always the person I go to when we need to hire new engineers into our projects. She has a great sense for recognising the talent and culture fit into our teams. I've never seen anyone structuring all the information as well as she is."
                </blockquote>
            </div>
              <div className="testimonial-author">
                <div className="author-info">
                  <h4 className="author-name">Robert Krejca</h4>
                  <p className="author-title">CEO and Culture Jockey</p>
                </div>
                <div className="company-badge">
                  <span>Tech Startup</span>
                </div>
              </div>
            </div>

            <div className="testimonial-card">
              <div className="testimonial-content">
                <div className="quote-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z"/>
                    <path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z"/>
                  </svg>
                </div>
                <blockquote className="testimonial-text">
                  "I've met dozens of recruiters who claimed to be 'determined to help us', but until I met Barbora, I didn't think they actually presented a real added value. Building employment branding, changing the way we approach our future employees, all that helped me to understand and appreciate the way Barbora works."
                </blockquote>
              </div>
              <div className="testimonial-author">
                <div className="author-info">
                  <h4 className="author-name">Jan Klusacek</h4>
                  <p className="author-title">CEO @ Cyber Fox</p>
                </div>
                <div className="company-badge">
                  <span>Cyber Fox</span>
                </div>
              </div>
            </div>

            <div className="testimonial-card">
              <div className="testimonial-content">
                <div className="quote-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z"/>
                    <path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z"/>
                  </svg>
                </div>
                <blockquote className="testimonial-text">
                  "Barbora was helping us on consulting basis and helped us hire 4 great engineers just in 2 months. I'm very happy with the result of our cooperation and definitely recommend Barbora to anyone who struggles to acquire talent on today's tough tech job market."
                </blockquote>
              </div>
              <div className="testimonial-author">
                <div className="author-info">
                  <h4 className="author-name">Marian Skvarek</h4>
                  <p className="author-title">Founder @ Realpad</p>
                </div>
                <div className="company-badge">
                  <span>Realpad</span>
                </div>
              </div>
            </div>

            <div className="testimonial-card">
              <div className="testimonial-content">
                <div className="quote-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z"/>
                    <path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z"/>
                  </svg>
                </div>
                <blockquote className="testimonial-text">
                  "Barbora is one of the very few agency consultants who has managed to get any Hires @Skype = must be good! Her friendly approach combined technical background and passion in technology I can recommend as interesting benefit for anyone who is looking for skilled IT professionals."
                </blockquote>
              </div>
              <div className="testimonial-author">
                <div className="author-info">
                  <h4 className="author-name">Miroslav Vasko</h4>
                  <p className="author-title">Partner @ nymble ltd, former @ Skype</p>
                </div>
                <div className="company-badge">
                  <span>Skype Alumni</span>
                </div>
              </div>
            </div>

            <div className="testimonial-card">
              <div className="testimonial-content">
                <div className="quote-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z"/>
                    <path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z"/>
                  </svg>
                </div>
                <blockquote className="testimonial-text">
                  "Regardless of whether we needed an in-house employee, a contractor, a developer or a tester, Barbora consistently met the challenge. Beyond her recruiting talents, she was able to provide invaluable HR consultancy. To anyone seeking a seasoned recruiter and HR adviser in Europe, I can't recommend Barbora enough."
                </blockquote>
              </div>
              <div className="testimonial-author">
                <div className="author-info">
                  <h4 className="author-name">Tadeáš Moravec</h4>
                  <p className="author-title">Senior Engineering Manager, former @ Akamai</p>
                </div>
                <div className="company-badge">
                  <span>Akamai Alumni</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="trusted-partners">
            <div className="partners-header">
              <div className="section-badge">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  <path d="M9 12l2 2 4-4"/>
                </svg>
                <span>Trusted Partners</span>
              </div>
              <h3>Trusted by Industry Leaders</h3>
              <p>These are companies where we successfully placed candidates using the proven methodologies that became the foundation of our training programs</p>
            </div>
            <div className="partner-logos">
              <div className="partner-logo">
                <span>Avast</span>
                <small>Cybersecurity</small>
              </div>
              <div className="partner-logo">
                <span>Skype</span>
                <small>Microsoft</small>
              </div>
              <div className="partner-logo">
                <span>Akamai</span>
                <small>CDN Leader</small>
              </div>
              <div className="partner-logo">
                <span>SuseLinux</span>
                <small>Enterprise Linux</small>
              </div>
              <div className="partner-logo">
              <span>BrandEmbassy</span>
                <small>Fintech</small>
              </div>
              <div className="partner-logo">
              <span>Angelcam</span>
                <small>AI Security</small>
              </div>
              <div className="partner-logo">
                <span>OPT On Demand</span>
                <small>IT Services</small>
              </div>
              <div className="partner-logo">
              <span>Easycore</span>
                <small>Technology</small>
              </div>
              <div className="partner-logo">
                <span>Vendavo</span>
                <small>Revenue Management</small>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="landing-cta">
        <div className="cta-content">
          <div className="cta-badge">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
            </svg>
            <span>Ready to Transform?</span>
          </div>
          <h2 className="cta-title">
            <span className="transform-text">Revolutionise</span> Your Recruitment Today
          </h2>
          <p className="cta-description">
            We've built this comprehensive training system to help you master the art of hiring elite talent - starting completely FREE. </p>
          <p className="cta-description">
            We're also offering a complimentary personalised strategy session to optimise your recruitment approach — if you're interested.
          </p>
              
          <div className="hero-stats">
            <div className="stat-item">
              <div className="stat-number">8</div>
              <div className="stat-label">Free Modules</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">8+</div>
              <div className="stat-label">Valuable Resources</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">4-8h</div>
              <div className="stat-label">Self-Paced Training</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">FREE</div>
              <div className="stat-label">To Start With</div>
            </div>
          </div>
              
          <div className="hero-actions">
            <button 
              onClick={() => setShowEmailSignup(true)}
              className="btn btn-primary btn-large"
            >
              Start Free Course Now
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14m-7-7l7 7-7 7"/>
              </svg>
            </button>
            <button 
              onClick={() => {
                const coursesSection = document.querySelector('.landing-courses');
                if (coursesSection) {
                  coursesSection.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              className="btn btn-secondary btn-large"
            >
              Review All Courses
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
                <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
              </svg>
            </button>
          </div>
              
          <div className="hero-trust">
            <p>No credit card required • Instant access • Start immediately</p>
            <div className="trust-logos">
              <span>1-on-1 Strategy Session</span>
              <span>500+ Placements</span>
              <span>20+ Years Experience</span>
              <span>Market Research Based</span>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="preview-footer">
        <div className="footer-content">
          <div className="footer-top">
            <div className="footer-logo">
              <span>© 2025 recberry Recruiter Program</span>
            </div>
            <div className="social-profiles">
              <a 
                href="https://cz.linkedin.com/company/recberry" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                LinkedIn
              </a>
              <a 
                href="https://cs-cz.facebook.com/recberry/" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                Facebook
              </a>
              <a 
                href="https://twitter.com/rec_berry" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                X / twitter
              </a>
            </div>
          </div>
          
          <div className="center-line"></div>
          
          <div className="rights">
            <p>
              ® all rights reserved. designed and developed by{' '}
              <a href="http://abaddion.com" target="_blank" rel="noopener noreferrer">
                abaddion
              </a>
            </p>
          </div>
          
          <div className="business">
            <p>
              recberry s.r.o. | IC: 04972236 | DIC: CZ04972236 | Tupolevova 741,
              Prague 9 Letnany | Incorporated in the Commercial Register kept by
              the City: Court in Prague, Section C, under file No.256441
            </p>
            <p style={{ marginTop: '1rem' }}>
              <Link to="/data" style={{ color: 'var(--orange)', textDecoration: 'none' }}>
                Data Protection Policy
              </Link>
            </p>
          </div>
        </div>
      </footer>

      {/* Waitlist Modal */}
      {waitlistModal.isOpen && waitlistModal.courseType && (
        <WaitlistForm
          courseType={waitlistModal.courseType}
          onClose={closeWaitlist}
          onSuccess={handleWaitlistSuccess}
        />
      )}

      {/* Email Signup Modal */}
      {showEmailSignup && (
        <EmailSignupModal
          onClose={() => setShowEmailSignup(false)}
          onSuccess={() => {
            setShowEmailSignup(false);
            // User will receive email with credentials
          }}
        />
      )}

      {/* Success Message */}
      {waitlistSuccess && (
        <div className="waitlist-success-overlay">
          <div className="waitlist-success-modal">
            <div className="success-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 12l2 2 4-4"/>
                <circle cx="12" cy="12" r="10"/>
              </svg>
            </div>
            <h3>You're on the waitlist!</h3>
            <p>We'll notify you as soon as the course becomes available.</p>
          </div>
        </div>
      )}

    </div>
  );
}

// PreviewPageWrapper and PreviewModule1Wrapper are now imported from separate files

// ============================================================================
// EMAIL SIGNUP MODAL
// ============================================================================

interface EmailSignupModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

function EmailSignupModal({ onClose, onSuccess }: EmailSignupModalProps) {
  const [formData, setFormData] = useState({
    email: '',
    emailConfirmation: '',
    name: '',
    company: '',
    position: '',
    recruiterType: '',
    fieldOfWork: '',
    teamSize: '',
    recruitmentTeamSize: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dataProtectionAccepted, setDataProtectionAccepted] = useState(false);
  const [showDataProtectionModal, setShowDataProtectionModal] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [emailMatchError, setEmailMatchError] = useState('');
  
  // Modal states for the three scenarios
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showExistingInterestModal, setShowExistingInterestModal] = useState(false);
  const [showAlreadyStartedModal, setShowAlreadyStartedModal] = useState(false);
  const [userHistory, setUserHistory] = useState<any>(null);
  const [mistakeEmail, setMistakeEmail] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validate data protection acceptance
      if (!dataProtectionAccepted) {
        setError('Please accept the Data Protection Policy to continue');
        setLoading(false);
        return;
      }

      // Validate email
      const emailValidation = validateEmail(formData.email);
      if (!emailValidation.isValid) {
        setError(emailValidation.error || 'Please enter a valid email address');
        setLoading(false);
        return;
      }

      // Validate email confirmation
      const matchValidation = validateEmailMatch(formData.email, formData.emailConfirmation);
      if (!matchValidation.isValid) {
        setError(matchValidation.error || 'Email addresses do not match');
        setLoading(false);
        return;
      }

      const response = await fetch(apiUrl('/api/free-course-signup'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          name: formData.name,
          company: formData.company,
          role: formData.position,
          recruiterType: formData.recruiterType,
          fieldOfWork: formData.fieldOfWork,
          teamSize: formData.teamSize,
          recruitmentTeamSize: formData.recruitmentTeamSize,
          message: formData.message
        }),
      });

      const data = await response.json();

      if (data.success) {
        // Handle the three scenarios based on user history
        const userHistory = data.data?.userHistory;
        setUserHistory(userHistory);
        
        if (userHistory?.scenario === 'existing_interest') {
          // Scenario 2: User has existing interest in waitlist or masterclass
          setShowExistingInterestModal(true);
        } else {
          // Scenario 1: New user
          setShowSuccessModal(true);
        }
      } else {
        // Handle scenario 3: User already started free course
        if (data.message === 'already_started_free_course') {
          setShowAlreadyStartedModal(true);
        } else {
          setError(data.message || 'Failed to sign up. Please try again.');
        }
      }
    } catch (error) {
      console.error('Email signup error:', error);
      setError('Failed to sign up. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleMistakeSubmit = async () => {
    if (!mistakeEmail.trim()) {
      alert('Please enter your email address');
      return;
    }

    try {
      // Send email to admin about the mistake
      const response = await fetch(apiUrl('/api/waitlist'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: mistakeEmail,
          name: 'Mistake Report',
          company: '',
          role: '',
          recruiterType: 'external', // Default value to satisfy validation
          fieldOfWork: 'IT', // Default value to satisfy validation
          teamSize: '',
          recruitmentTeamSize: '',
          message: `User reports mistake: ${formData.email} says they haven't started the free course yet`,
          courseType: 'free'
        }),
      });

      if (response.ok) {
        alert('Thank you! We\'ll send you an invitation within 24 hours.');
        setShowAlreadyStartedModal(false);
        onSuccess();
      } else {
        alert('Something went wrong. Please try again or contact us at barbora@recberry.com');
      }
    } catch (error) {
      console.error('Mistake report error:', error);
      alert('Something went wrong. Please try again or contact us at barbora@recberry.com');
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '2rem',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem'
        }}>
          <h2 style={{ margin: 0, color: 'var(--dark)', display: 'flex', alignItems: 'center', gap: '0.5rem', fontFamily: 'var(--montserrat)' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--orange)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
              <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
            </svg>
            Start Your Free Course
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: 'var(--gray)'
            }}
          >
            ×
          </button>
        </div>

        <p style={{ color: 'var(--gray)', marginBottom: '1.5rem', lineHeight: 1.6, fontFamily: 'var(--montserrat)' }}>
          Get instant access to our complete free course with 8 modules. Just enter your details below to start learning immediately.
        </p>

        {error && (
          <div style={{
            background: '#fee2e2',
            color: '#dc2626',
            padding: '0.75rem',
            borderRadius: '8px',
            marginBottom: '1rem',
            border: '1px solid #fecaca',
            fontFamily: 'var(--montserrat)'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'var(--dark)', fontFamily: 'var(--montserrat)' }}>
              Your Name *
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Enter your full name"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '1rem',
                fontFamily: 'var(--raleway)'
              }}
            />
          </div>

          <EmailInput
            value={formData.email}
            onChange={(value) => {
              setFormData(prev => ({ ...prev, email: value }));
              setEmailMatchError('');
            }}
            onValidationChange={setIsEmailValid}
            placeholder="Enter your email address"
            required
            label="Email Address"
          />

          <EmailInput
            value={formData.emailConfirmation}
            onChange={(value) => {
              setFormData(prev => ({ ...prev, emailConfirmation: value }));
              if (value && formData.email) {
                const matchValidation = validateEmailMatch(formData.email, value);
                setEmailMatchError(matchValidation.isValid ? '' : matchValidation.error || '');
              } else {
                setEmailMatchError('');
              }
            }}
            placeholder="Confirm your email address"
            required
            label="Confirm Email Address"
          />

          {emailMatchError && (
            <div style={{
              marginTop: '-1rem',
              marginBottom: '1rem',
              color: '#dc2626',
              fontSize: '0.875rem',
              fontFamily: 'var(--montserrat)',
              padding: '0.5rem',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px'
            }}>
              {emailMatchError}
            </div>
          )}

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'var(--dark)', fontFamily: 'var(--montserrat)' }}>
              Company Name *
            </label>
            <input
              type="text"
              required
              value={formData.company}
              onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
              placeholder="Enter your company name"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '1rem',
                fontFamily: 'var(--raleway)'
              }}
            />
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'var(--dark)', fontFamily: 'var(--montserrat)' }}>
              Your Position *
            </label>
            <input
              type="text"
              required
              value={formData.position}
              onChange={(e) => setFormData(prev => ({ ...prev, position: e.target.value }))}
              placeholder="e.g., Senior Recruiter, TA Manager, HR Director"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '1rem',
                fontFamily: 'var(--raleway)'
              }}
            />
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'var(--dark)', fontFamily: 'var(--montserrat)' }}>
              Are you an external or internal recruiter? *
            </label>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="recruiterType"
                  value="external"
                  checked={formData.recruiterType === 'external'}
                  onChange={(e) => setFormData(prev => ({ ...prev, recruiterType: e.target.value }))}
                  required
                  style={{ margin: 0 }}
                />
                <span style={{ fontFamily: 'var(--montserrat)' }}>External Recruiter</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="recruiterType"
                  value="internal"
                  checked={formData.recruiterType === 'internal'}
                  onChange={(e) => setFormData(prev => ({ ...prev, recruiterType: e.target.value }))}
                  required
                  style={{ margin: 0 }}
                />
                <span style={{ fontFamily: 'var(--montserrat)' }}>Internal Recruiter</span>
              </label>
            </div>
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'var(--dark)', fontFamily: 'var(--montserrat)' }}>
              What field do you hire for? *
            </label>
            <select
              value={formData.fieldOfWork}
              onChange={(e) => setFormData(prev => ({ ...prev, fieldOfWork: e.target.value }))}
              required
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '1rem',
                backgroundColor: 'white',
                fontFamily: 'var(--raleway)'
              }}
            >
              <option value="">Select hiring field</option>
              <option value="IT">IT / Technology</option>
              <option value="Sales">Sales</option>
              <option value="Marketing">Marketing</option>
              <option value="Finance">Finance / Banking</option>
              <option value="Healthcare">Healthcare</option>
              <option value="Manufacturing">Manufacturing</option>
              <option value="Retail">Retail</option>
              <option value="Education">Education</option>
              <option value="Multilingual">Multilingual / International</option>
              <option value="All">All fields / General</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'var(--dark)', fontFamily: 'var(--montserrat)' }}>
              Your Team Size
            </label>
            <select
              value={formData.teamSize}
              onChange={(e) => setFormData(prev => ({ ...prev, teamSize: e.target.value }))}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '1rem',
                backgroundColor: 'white',
                fontFamily: 'var(--raleway)'
              }}
            >
              <option value="">Select team size (optional)</option>
              <option value="1-5">1-5 people</option>
              <option value="6-10">6-10 people</option>
              <option value="11-20">11-20 people</option>
              <option value="21-50">21-50 people</option>
              <option value="50+">50+ people</option>
            </select>
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'var(--dark)', fontFamily: 'var(--montserrat)' }}>
              Recruitment Team Size
            </label>
            <select
              value={formData.recruitmentTeamSize}
              onChange={(e) => setFormData(prev => ({ ...prev, recruitmentTeamSize: e.target.value }))}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '1rem',
                backgroundColor: 'white',
                fontFamily: 'var(--raleway)'
              }}
            >
              <option value="">Select recruitment team size (optional)</option>
              <option value="1-2">1-2 people</option>
              <option value="3-5">3-5 people</option>
              <option value="6-10">6-10 people</option>
              <option value="11-20">11-20 people</option>
              <option value="20+">20+ people</option>
            </select>
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'var(--dark)', fontFamily: 'var(--montserrat)' }}>
              Additional Message (Optional)
            </label>
            <textarea
              value={formData.message}
              onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
              rows={3}
              placeholder="Tell us about your recruitment challenges or specific needs..."
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '1rem',
                fontFamily: 'var(--raleway)',
                resize: 'vertical'
              }}
            />
          </div>

          {/* Data Protection Checkbox */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', cursor: 'pointer', fontFamily: 'var(--montserrat)', fontSize: '0.875rem' }}>
              <input
                type="checkbox"
                checked={dataProtectionAccepted}
                onChange={(e) => setDataProtectionAccepted(e.target.checked)}
                style={{ marginTop: '2px', accentColor: 'var(--orange)' }}
                required
              />
              <span>
                I have read and accept the{' '}
                <button
                  type="button"
                  onClick={() => setShowDataProtectionModal(true)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--orange)',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    fontFamily: 'var(--montserrat)',
                    fontSize: '0.875rem'
                  }}
                >
                  Data Protection Policy
                </button>
                {' '}*
              </span>
            </label>
          </div>

          <div style={{ display: 'flex', gap: '1rem' }}>
            <button
              type="button"
              onClick={onClose}
                     style={{
                       flex: 1,
                       background: '#f3f4f6',
                       color: 'var(--dark)',
                       border: 'none',
                       padding: '0.75rem 1.5rem',
                       borderRadius: '8px',
                       fontSize: '1rem',
                       cursor: 'pointer',
                       fontFamily: 'var(--montserrat)'
                     }}
                   >
                     Cancel
                   </button>
                   <button
                     type="submit"
                     disabled={loading}
                     style={{
                       flex: 2,
                       background: loading ? '#9ca3af' : 'var(--orange)',
                       color: 'var(--dark)',
                       border: 'none',
                       padding: '0.75rem 1.5rem',
                       borderRadius: '8px',
                       fontSize: '1rem',
                       cursor: loading ? 'not-allowed' : 'pointer',
                       fontFamily: 'var(--montserrat)'
                     }}
                   >
                     {loading ? 'Signing Up...' : 'Start Free Course'}
            </button>
          </div>
        </form>
      </div>
      
      {/* Data Protection Modal */}
      {showDataProtectionModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
          padding: '1rem'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '2rem',
            maxWidth: '800px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1.5rem'
            }}>
              <h2 style={{ margin: 0, color: 'var(--dark)', fontFamily: 'var(--montserrat)' }}>
                Data Protection Policy
              </h2>
              <button
                onClick={() => setShowDataProtectionModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: 'var(--gray)'
                }}
              >
                ×
              </button>
            </div>
            
            <div style={{ lineHeight: '1.8', fontSize: '1rem', fontFamily: 'var(--montserrat)' }}>
              <p style={{ marginBottom: '1rem', fontSize: '0.9rem', color: 'var(--gray)', fontWeight: '600' }}>
                <strong>Recberry s.r.o. Data Protection Policy</strong><br/>
                <strong>Version (Effective): October 24, 2025</strong>
              </p>
              <p style={{ marginBottom: '1rem' }}>
                Your privacy and data protection are our top priorities at Recberry s.r.o. This Data Protection Policy applies to all data collections and processing activities conducted by Recberry s.r.o., particularly through our website, email communications, and marketing services. For purposes of this Policy, any reference to "Recberry," "we," "us," or "our" means Recberry s.r.o. and its affiliates. This Policy describes how we collect, use, share, and otherwise process your personal data in compliance with the General Data Protection Regulation (GDPR) (EU Regulation 2016/679). It also outlines your choices regarding the use, access, correction, and deletion of your personal data. The use of personal data is limited to the purposes outlined herein, such as providing marketing services, sending newsletters, and contacting you as requested.
              </p>
              <p style={{ marginBottom: '1rem' }}>
                We assure you that your personal data is used solely within our company to promote our products and services through activities like newsletters and direct communications. We do not share, sell, or transfer your data to any external parties for their own purposes beyond the necessary service providers outlined in this Policy. You can unsubscribe from our communications or request data deletion at any time, and we will promptly honor such requests.
              </p>
              
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>1. Data Controller</h3>
              <p style={{ marginBottom: '1rem' }}>
                <strong>Recberry s.r.o.</strong><br/>
                IČ: 04972236 | DIČ: CZ04972236<br/>
                Tupolevova 741, 199 00 Prague 9 - Letňany, Czech Republic<br/>
                Incorporated in the Commercial Register kept by the Municipal Court in Prague, Section C, File No. 256441<br/>
                <strong>Data Protection Contact:</strong> Barbora Jensik<br/>
                <strong>Email:</strong> barbora@recberry.com
              </p>
              
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>2. Legal Basis for Processing</h3>
              <p style={{ marginBottom: '1rem' }}>
                We process your personal data based on your explicit consent under Article 6(1)(a) of the GDPR. This consent is freely given, specific, informed, and unambiguous. You may withdraw your consent at any time without affecting the lawfulness of processing based on consent before its withdrawal. No automated individual decision-making, including profiling under Article 22 GDPR, occurs in our processing activities.
              </p>
              
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>3. Collected Personal Data and How We Use It</h3>
              <p style={{ marginBottom: '1rem' }}>
                We may collect and/or process the following categories of personal data from individuals who interact with our services, such as newsletter subscribers, website visitors, or marketing contacts:
              </p>
              
              <div style={{ 
                background: '#f8f9fa', 
                padding: '1rem', 
                borderRadius: '8px', 
                marginBottom: '1rem',
                overflowX: 'auto'
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #ddd' }}>
                      <th style={{ padding: '0.4rem', textAlign: 'left', fontWeight: '600' }}>Type of Data</th>
                      <th style={{ padding: '0.4rem', textAlign: 'left', fontWeight: '600' }}>Description</th>
                      <th style={{ padding: '0.4rem', textAlign: 'left', fontWeight: '600' }}>Duration</th>
                      <th style={{ padding: '0.4rem', textAlign: 'left', fontWeight: '600' }}>Entity</th>
                      <th style={{ padding: '0.4rem', textAlign: 'left', fontWeight: '600' }}>Purpose</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '0.4rem', fontWeight: '500' }}>Identifiers</td>
                      <td style={{ padding: '0.4rem' }}>Names, email addresses, phone numbers, job titles, employers, contact details, IP addresses</td>
                      <td style={{ padding: '0.4rem' }}>See Sec. 7</td>
                      <td style={{ padding: '0.4rem' }}>Recberry s.r.o.</td>
                      <td style={{ padding: '0.4rem' }}>See Sec. 3.2.1-6</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '0.4rem', fontWeight: '500' }}>IT Information</td>
                      <td style={{ padding: '0.4rem' }}>IP addresses, device information, browser type, location data, cookies</td>
                      <td style={{ padding: '0.4rem' }}>See Sec. 7</td>
                      <td style={{ padding: '0.4rem' }}>Recberry s.r.o.</td>
                      <td style={{ padding: '0.4rem' }}>See Sec. 3.2.1-6</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '0.4rem', fontWeight: '500' }}>Special Categories</td>
                      <td style={{ padding: '0.4rem' }}>Sensitive data under Art. 9 GDPR, only if explicitly provided and necessary</td>
                      <td style={{ padding: '0.4rem' }}>See Sec. 7</td>
                      <td style={{ padding: '0.4rem' }}>Recberry s.r.o.</td>
                      <td style={{ padding: '0.4rem' }}>See Sec. 3.2.1-6</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p style={{ marginBottom: '1rem' }}>
                Personal data is collected and used for Recberry's legitimate business purposes as the data controller, including fulfilling contractual obligations and complying with legal requirements. We process data only for the purposes described in this Policy or for which we have obtained your consent. Specifically, we use personal data for:
              </p>
              <ul style={{ paddingLeft: '1.5rem', marginBottom: '1rem' }}>
                <li style={{ marginBottom: '0.5rem' }}><strong>3.2.1.</strong> Providing marketing services, such as contacting you about our products or events.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>3.2.2.</strong> Sending newsletters and promotional materials related to our offerings.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>3.2.3.</strong> Facilitating communications and responding to inquiries.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>3.2.4.</strong> Analyzing trends to improve our services (e.g., website usage statistics).</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>3.2.5.</strong> For data security and compliance purposes, including internal audits.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>3.2.6.</strong> For statistical and analytical purposes, including product enhancement, but only in aggregated or anonymized forms.</li>
              </ul>
              
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>4. How We Use Cookies and Similar Technologies</h3>
              <p style={{ marginBottom: '1rem' }}>
                Cookies and similar technologies (e.g., pixels, web beacons) are used by Recberry and our partners to analyze trends, administer the website, track user movements, and gather demographic information. We categorize cookies as follows:
              </p>
              <ul style={{ paddingLeft: '1.5rem', marginBottom: '1rem' }}>
                <li style={{ marginBottom: '0.5rem' }}><strong>Essential cookies:</strong> Necessary for site functionality (e.g., session management).</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>Analytical cookies:</strong> For performance insights (e.g., Google Analytics, anonymized data).</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>Marketing cookies:</strong> For personalized content delivery.</li>
              </ul>
              <p style={{ marginBottom: '1rem' }}>
                We obtain your consent for non-essential cookies via a consent banner on our website, in line with GDPR and ePrivacy Directive requirements. You can manage or withdraw consent at any time through browser settings or by contacting us.
              </p>
              
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>5. Access to Personal Data</h3>
              <p style={{ marginBottom: '1rem' }}>
                Upon request and within one month (extendable to two months for complex requests, as per Art. 12 GDPR), we will provide information about your personal data we hold, including confirmation of processing, categories of data, purposes, recipients, and storage period. You can request correction, amendment, deletion, or a copy of your data in a structured, commonly used format. Contact barbora@recberry.com for these requests.
              </p>
              
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>6. Data Recipients and Transfers</h3>
              <p style={{ marginBottom: '1rem' }}>
                Recipients of your personal data are limited to sub-processors providing marketing services (e.g., email platforms) under strict GDPR-compliant data processing agreements. We have no intention to transfer data to non-EU countries or international organizations without adequate safeguards, such as Standard Contractual Clauses approved by the European Commission. All transfers, if any, are documented and ensure an equivalent level of protection. We do not share data with any other third parties for their independent use.
              </p>
              
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>7. Data Retention</h3>
              <p style={{ marginBottom: '1rem' }}>
                Personal data is retained for up to 5 years from the date of collection or until you withdraw your consent, whichever occurs first. We retain data only as long as necessary for the purposes for which it was collected, to comply with legal obligations (e.g., tax or accounting requirements), resolve disputes, or enforce agreements. Upon fulfillment of these purposes, we securely delete or anonymize the data.
              </p>
              
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>8. Data Security</h3>
              <p style={{ marginBottom: '1rem' }}>
                We employ reasonable and appropriate security measures to protect against loss, misuse, unauthorized access, disclosure, alteration, or destruction of personal data. These include:
              </p>
              <ul style={{ paddingLeft: '1.5rem', marginBottom: '1rem' }}>
                <li style={{ marginBottom: '0.5rem' }}><strong>Technical measures:</strong> Encryption of data in transit and at rest, firewalls, secure servers.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>Organizational measures:</strong> Access controls (role-based), employee training on GDPR compliance, confidentiality agreements.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>Physical measures:</strong> Secure facilities for any physical records.</li>
              </ul>
              <p style={{ marginBottom: '1rem' }}>
                You are responsible for keeping any credentials secure. No method of transmission or storage is 100% secure; however, we regularly review and update our measures to align with industry standards.
              </p>
              
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>9. Links to Third-Party Sites</h3>
              <p style={{ marginBottom: '1rem' }}>
                Our website may contain links to third-party sites whose privacy practices differ from ours. If you submit personal data to these sites, it is governed by their policies, and we are not responsible for their actions. We encourage you to review third-party privacy policies before interacting.
              </p>
              
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>10. No Services for Minors</h3>
              <p style={{ marginBottom: '1rem' }}>
                Our services are not directed to individuals under 16 years of age. We do not knowingly collect personal data from minors. If we become aware of such collection, we will take steps to delete the data promptly.
              </p>
              
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>11. Your Rights Under GDPR</h3>
              <p style={{ marginBottom: '1rem' }}>
                In accordance with GDPR, you have the following rights regarding your personal data:
              </p>
              <ul style={{ paddingLeft: '1.5rem', marginBottom: '1rem' }}>
                <li style={{ marginBottom: '0.5rem' }}><strong>Right to Access (Art. 15):</strong> Obtain confirmation of processing and a copy of your data.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>Right to Rectification (Art. 16):</strong> Correct inaccurate or incomplete data.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>Right to Erasure ("Right to be Forgotten," Art. 17):</strong> Request deletion when data is no longer necessary or consent is withdrawn.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>Right to Restriction of Processing (Art. 18):</strong> Limit processing under certain conditions.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>Right to Object (Art. 21):</strong> Object to processing for direct marketing or based on legitimate interests.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>Right to Data Portability (Art. 20):</strong> Receive your data in a structured, machine-readable format.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>Right to Withdraw Consent (Art. 7):</strong> Revoke consent at any time, without affecting prior lawfulness.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>Right Not to Be Subject to Automated Decisions (Art. 22):</strong> Not applicable, as we do not engage in such processing.</li>
              </ul>
              <p style={{ marginBottom: '1rem' }}>
                To exercise these rights, contact barbora@recberry.com. We will respond within one month. You also have the right to lodge a complaint with a supervisory authority, such as the Czech Office for Personal Data Protection (Úřad pro ochranu osobních údajů, www.uoou.cz).
              </p>
              
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>12. GDPR Compliance Checklist</h3>
              <p style={{ marginBottom: '1rem' }}>
                To demonstrate our commitment to GDPR compliance, we maintain the following practices:
              </p>
              <ul style={{ paddingLeft: '1.5rem', marginBottom: '1rem' }}>
                <li style={{ marginBottom: '0.5rem' }}><strong>Lawfulness, Fairness, and Transparency:</strong> All processing is based on consent and clearly explained in this Policy.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>Purpose Limitation:</strong> Data is used only for specified marketing purposes related to our products.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>Data Minimization:</strong> We collect only necessary data (e.g., name and email for newsletters).</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>Accuracy:</strong> We update data upon request and verify sources.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>Storage Limitation:</strong> Retention is limited to 5 years or less, with regular reviews.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>Integrity and Confidentiality:</strong> Security measures protect against breaches; incidents are reported within 72 hours to authorities if required (Art. 33).</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>Accountability:</strong> We maintain records of processing activities (Art. 30), conduct data protection impact assessments for high-risk activities (Art. 35), and appoint a data protection contact (Barbora Jensik).</li>
              </ul>
              <p style={{ marginBottom: '1rem' }}>
                We conduct annual internal audits to ensure ongoing compliance.
              </p>
              
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>13. Changes to This Policy</h3>
              <p style={{ marginBottom: '1rem' }}>
                We may update this Policy to reflect changes in our practices or legal requirements. Material changes will be notified via email or a prominent notice on our website at least 30 days before they take effect. We encourage you to review this Policy periodically.
              </p>
              
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>14. Data Transfers Outside the EEA</h3>
              <p style={{ marginBottom: '1rem' }}>
                Personal data is processed within the EEA. If any transfers outside the EEA are necessary (e.g., for sub-processors), we use Standard Contractual Clauses or other approved mechanisms to ensure adequate protection, as required by Chapter V of the GDPR.
              </p>
              
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>15. Compliance and Supervisory Authority</h3>
              <p style={{ marginBottom: '1rem' }}>
                We comply fully with GDPR and are registered with the relevant authorities. You have the right to lodge a complaint with the Czech Office for Personal Data Protection (www.uoou.cz) if you believe your rights have been infringed.
              </p>
              
              <div style={{ 
                background: 'var(--light-gray)', 
                padding: '1.5rem', 
                borderRadius: '8px',
                marginTop: '1.5rem'
              }}>
                <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>16. Data Protection Contact</h3>
                <p style={{ margin: 0, fontSize: '0.9rem' }}>
                  For any questions, concerns, or to exercise your rights, contact:<br/>
                  <strong>Barbora Jensik</strong><br/>
                  <strong>Email:</strong> barbora@recberry.com<br/>
                  <strong>Address:</strong> Recberry s.r.o., Tupolevova 741, 199 00 Prague 9 - Letňany, Czech Republic
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Scenario 1: New User Success Modal */}
      {showSuccessModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1002,
          padding: '1rem'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '2rem',
            maxWidth: '500px',
            width: '100%',
            textAlign: 'center',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          }}>
            <div style={{
              fontSize: '3rem',
              marginBottom: '1rem',
              display: 'flex',
              justifyContent: 'center'
            }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--orange)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
                <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
              </svg>
            </div>
            <h2 style={{ 
              margin: '0 0 1rem 0', 
              color: 'var(--dark)', 
              fontFamily: 'var(--montserrat)',
              fontSize: '1.5rem'
            }}>
              Welcome to Your Free Course!
            </h2>
            <p style={{ 
              color: 'var(--gray)', 
              marginBottom: '1.5rem', 
              lineHeight: 1.6, 
              fontFamily: 'var(--montserrat)' 
            }}>
              <strong>Check your inbox</strong> for login details to start your free course. We're excited to help you transform your recruitment skills!
            </p>
            <button
              onClick={() => {
                setShowSuccessModal(false);
                onSuccess();
              }}
              style={{
                background: 'var(--orange)',
                color: 'var(--dark)',
                border: 'none',
                padding: '0.75rem 2rem',
                borderRadius: '8px',
                fontSize: '1rem',
                cursor: 'pointer',
                fontFamily: 'var(--montserrat)',
                fontWeight: '600'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Scenario 2: Existing Interest Modal */}
      {showExistingInterestModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1002,
          padding: '1rem'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '2rem',
            maxWidth: '500px',
            width: '100%',
            textAlign: 'center',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          }}>
            <div style={{
              fontSize: '3rem',
              marginBottom: '1rem',
              display: 'flex',
              justifyContent: 'center'
            }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--orange)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
                <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
              </svg>
            </div>
            <h2 style={{ 
              margin: '0 0 1rem 0', 
              color: 'var(--dark)', 
              fontFamily: 'var(--montserrat)',
              fontSize: '1.5rem'
            }}>
              Great to See You Again!
            </h2>
            <p style={{ 
              color: 'var(--gray)', 
              marginBottom: '1.5rem', 
              lineHeight: 1.6, 
              fontFamily: 'var(--montserrat)' 
            }}>
              We see you've already joined our waiting list{userHistory?.hasTeamInterest ? ' or expressed team interest' : ''}. 
              We're thrilled you're taking the next step! <strong>Check your inbox</strong> for login details to start your free course. 
              We believe in delivering quality content that will make you even more excited about our advanced courses.
            </p>
            <button
              onClick={() => {
                setShowExistingInterestModal(false);
                onSuccess();
              }}
              style={{
                background: 'var(--orange)',
                color: 'var(--dark)',
                border: 'none',
                padding: '0.75rem 2rem',
                borderRadius: '8px',
                fontSize: '1rem',
                cursor: 'pointer',
                fontFamily: 'var(--montserrat)',
                fontWeight: '600'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Scenario 3: Already Started Free Course Modal */}
      {showAlreadyStartedModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1002,
          padding: '1rem'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '2rem',
            maxWidth: '500px',
            width: '100%',
            textAlign: 'center',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          }}>
            <div style={{
              fontSize: '3rem',
              marginBottom: '1rem',
              display: 'flex',
              justifyContent: 'center'
            }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--orange)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
                <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
              </svg>
            </div>
            <h2 style={{ 
              margin: '0 0 1rem 0', 
              color: 'var(--dark)', 
              fontFamily: 'var(--montserrat)',
              fontSize: '1.5rem'
            }}>
              Oops! Already Started?
            </h2>
            <p style={{ 
              color: 'var(--gray)', 
              marginBottom: '1.5rem', 
              lineHeight: 1.6, 
              fontFamily: 'var(--montserrat)' 
            }}>
              It looks like you've already taken or requested the free course. If this is a mistake, 
              please fill in your email below and we'll send you an invitation within 24 hours.
            </p>
            <EmailInput
              value={mistakeEmail}
              onChange={setMistakeEmail}
              placeholder="Enter your email address"
              required
              label="Your Email Address"
            />
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                onClick={() => setShowAlreadyStartedModal(false)}
                style={{
                  flex: 1,
                  background: '#f3f4f6',
                  color: 'var(--dark)',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  fontFamily: 'var(--montserrat)'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleMistakeSubmit}
                style={{
                  flex: 1,
                  background: 'var(--orange)',
                  color: 'var(--dark)',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  fontFamily: 'var(--montserrat)',
                  fontWeight: '600'
                }}
              >
                Send Invitation
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// WRAPPER COMPONENTS FOR USER ROUTES
// ============================================================================

interface UserRouteProps {
  user: User | null;
  onLogout: () => void;
}

function UserDashboardRoute({ user, onLogout }: UserRouteProps) {
  const { userId } = useParams<{ userId: string }>();
  
  if (!user || user.id !== userId) {
    return <Navigate to="/auth" replace />;
  }
  
  // If admin, redirect to admin dashboard to maintain admin navbar
  if (user.role === 'admin') {
    return <Navigate to="/admin/dashboard" replace />;
  }
  
  return (
    <UserLayout user={user} onLogout={onLogout}>
      <Dashboard user={user} onLogout={onLogout} />
    </UserLayout>
  );
}

function UserProgressRoute({ user, onLogout }: UserRouteProps) {
  const { userId } = useParams<{ userId: string }>();
  
  if (!user || user.id !== userId) {
    return <Navigate to="/auth" replace />;
  }
  
  // If admin, redirect to admin progress to maintain admin navbar
  if (user.role === 'admin') {
    return <Navigate to="/admin/progress" replace />;
  }
  
  return (
    <UserLayout user={user} onLogout={onLogout}>
      <ProgressDashboard user={user} onLogout={onLogout} />
    </UserLayout>
  );
}

function UserResourcesRoute({ user, onLogout }: UserRouteProps) {
  const { userId } = useParams<{ userId: string }>();
  
  if (!user || user.id !== userId) {
    return <Navigate to="/auth" replace />;
  }
  
  // If admin, redirect to admin resources to maintain admin navbar
  if (user.role === 'admin') {
    return <Navigate to="/admin/resources" replace />;
  }
  
  return (
    <UserLayout user={user} onLogout={onLogout}>
      <div>Resources Page - Coming Soon</div>
    </UserLayout>
  );
}

function UserAccountRoute({ user, onLogout }: UserRouteProps) {
  const { userId } = useParams<{ userId: string }>();
  
  if (!user || user.id !== userId) {
    return <Navigate to="/auth" replace />;
  }
  
  return (
    <UserLayout user={user} onLogout={onLogout}>
      <UserAccount user={user} onLogout={onLogout} />
    </UserLayout>
  );
}

function UserModuleRoute({ user, onLogout }: UserRouteProps) {
  const { userId, moduleId } = useParams<{ userId: string; moduleId: string }>();
  
  if (!user || user.id !== userId) {
    return <Navigate to="/auth" replace />;
  }
  
  // Route to specific module components for modules 1, 2, 3, and 4
  if (moduleId === '1') {
    return <Navigate to={`/user/${userId}/module1`} replace />;
  }
  if (moduleId === '2') {
    return <Navigate to={`/user/${userId}/module2`} replace />;
  }
  if (moduleId === '3') {
    return <Navigate to={`/user/${userId}/module3`} replace />;
  }
      if (moduleId === '4') {
        return <Navigate to={`/user/${userId}/module4`} replace />;
      }
      if (moduleId === '5') {
        return <Navigate to={`/user/${userId}/module5`} replace />;
      }
      if (moduleId === '6') {
        return <Navigate to={`/user/${userId}/module6`} replace />;
      }
      if (moduleId === '7') {
        return <Navigate to={`/user/${userId}/module7`} replace />;
      }
      if (moduleId === '8') {
        return <Navigate to={`/user/${userId}/module8`} replace />;
      }
      
      // For other modules, use the generic ModuleContent
  return (
    <UserLayout user={user} onLogout={onLogout}>
      <ModuleContent user={user} onLogout={onLogout} />
    </UserLayout>
  );
}

function UserModule2Route({ user, onLogout }: UserRouteProps) {
  const { userId } = useParams<{ userId: string }>();
  
  if (!user || user.id !== userId) {
    return <Navigate to="/auth" replace />;
  }
  
  // If admin, use AdminLayout to maintain admin navbar
  const Layout = user.role === 'admin' ? AdminLayout : UserLayout;
  const isAdminMode = user.role === 'admin';
  
  return (
    <Layout user={user} onLogout={onLogout}>
      <Module2Content user={user} onLogout={onLogout} isAdminMode={isAdminMode} />
    </Layout>
  );
}

function UserLessonRoute({ user, onLogout }: UserRouteProps) {
  const { userId } = useParams<{ userId: string }>();
  
  if (!user || user.id !== userId) {
    return <Navigate to="/auth" replace />;
  }
  
  return (
    <UserLayout user={user} onLogout={onLogout}>
      <LessonContent user={user} onLogout={onLogout} />
    </UserLayout>
  );
}

function UserModule1Route({ user, onLogout }: UserRouteProps) {
  const { userId } = useParams<{ userId: string }>();
  
  if (!user || user.id !== userId) {
    return <Navigate to="/auth" replace />;
  }
  
  // If admin, use AdminLayout to maintain admin navbar
  const Layout = user.role === 'admin' ? AdminLayout : UserLayout;
  const isAdminMode = user.role === 'admin';
  
  return (
    <Layout user={user} onLogout={onLogout}>
      <Module1ContentNew user={user} onLogout={onLogout} isAdminMode={isAdminMode} />
    </Layout>
  );
}

function UserModule6Route({ user, onLogout }: UserRouteProps) {
  const { userId } = useParams<{ userId: string }>();
  
  if (!user || user.id !== userId) {
    return <Navigate to="/auth" replace />;
  }
  
  // If admin, use AdminLayout to maintain admin navbar
  const Layout = user.role === 'admin' ? AdminLayout : UserLayout;
  const isAdminMode = user.role === 'admin';
  
  return (
    <Layout user={user} onLogout={onLogout}>
      <Module6Content user={user} onLogout={onLogout} isAdminMode={isAdminMode} />
    </Layout>
  );
}

function UserModule7Route({ user, onLogout }: UserRouteProps) {
  const { userId } = useParams<{ userId: string }>();
  
  if (!user || user.id !== userId) {
    return <Navigate to="/auth" replace />;
  }
  
  return (
    <UserLayout user={user} onLogout={onLogout}>
      <Module7Content user={user} onLogout={onLogout} isAdminMode={false} />
    </UserLayout>
  );
}

function UserModule8Route({ user, onLogout }: UserRouteProps) {
  const { userId } = useParams<{ userId: string }>();
  
  if (!user || user.id !== userId) {
    return <Navigate to="/auth" replace />;
  }
  
  // If admin, use AdminLayout to maintain admin navbar
  const Layout = user.role === 'admin' ? AdminLayout : UserLayout;
  const isAdminMode = user.role === 'admin';
  
  return (
    <Layout user={user} onLogout={onLogout}>
      <Module8Content user={user} onLogout={onLogout} isAdminMode={isAdminMode} />
    </Layout>
  );
}

export default App;